<template>
  <ConfirmDialog
    :open="open"
    max-height="90vh"
    :disabled-confirm-action="isValidatingStorage || isCreatingStorage"
    :confirm-action-loading="isValidatingStorage || isCreatingStorage"
    class-name="storage-creation-dialog"
    @cancel="close"
    @confirm="onSubmitGlobal"
  >
    <template #title> Storage</template>
    <template #detail> View and configure storage options </template>
    <template #confirm-text>{{ confirmText }}</template>

    <template #body>
      <div class="form__content">
        <StorageProviderSelect
          class="storage__field"
          :value="provider"
          label="Storage provider"
          @input="provider = $event"
        />
        <AwsStorageForm
          v-if="provider === StorageProvider.AWS"
          ref="awsStorageForm"
          :value="storage"
          @canceled="close"
          @submitted="onSubmit"
        />
        <WasabiStorageForm
          v-if="provider === StorageProvider.Wasabi"
          ref="wasabiStorageForm"
          :value="storage"
          @canceled="close"
          @submitted="onSubmit"
        />
        <GcpStorageForm
          v-else-if="provider === StorageProvider.GCP"
          ref="gcpStorageForm"
          :value="storage"
          @canceled="close"
          @submitted="onSubmit"
        />
        <AzureStorageForm
          v-else-if="provider === StorageProvider.Azure"
          ref="azureStorageForm"
          :value="storage"
          @canceled="close"
          @submitted="onSubmit"
        />
        <MinioStorageForm
          v-else-if="provider === StorageProvider.Minio"
          ref="minioStorageForm"
          :value="storage"
          @canceled="close"
          @submitted="onSubmit"
        />
      </div>
    </template>
  </ConfirmDialog>
</template>

<script lang="ts">
export default {
  name: 'StorageModal',
}
</script>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useToast } from '@/uiKit/Toast/useToast'
import AwsStorageForm from '@/components/Storage/Forms/AwsStorageForm.vue'
import WasabiStorageForm from '@/components/Storage/Forms/WasabiStorageForm.vue'
import AzureStorageForm from '@/components/Storage/Forms/AzureStorageForm.vue'
import GcpStorageForm from '@/components/Storage/Forms/GcpStorageForm.vue'
import MinioStorageForm from '@/components/Storage/Forms/MinioStorageForm.vue'
import StorageProviderSelect from '@/components/Storage/StorageProviderSelect.vue'
import { StorageProvider } from '@/store/types'
import type { StoragePayload } from '@/store/types'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useExternalStorageStore } from '@/modules/ExternalStorage/useExternalStorageStore'
import { setStorageAsDefault } from '@/backend/darwin/setStorageAsDefault'
import { updateStorage } from '@/backend/darwin/updateStorage'
import { createStorage } from '@/backend/darwin/createStorage'
import { validateStorage } from '@/backend/darwin/validateStorage'
import { useBillingInfoLoader } from '@/modules/Billing/useBillingInfoLoader'

const props = withDefaults(
  defineProps<{
    storage?: StoragePayload | null
    open: boolean
  }>(),
  { storage: null, open: false },
)

const emit = defineEmits<{
  (e: 'closed'): void
  (e: 'update:open', value: boolean): void
}>()

const updateOpen = (value: boolean): void => emit('update:open', value)

const toast = useToast()

const provider = ref(
  props.storage && props.storage.provider ? props.storage.provider : StorageProvider.AWS,
)

const awsStorageForm = ref<typeof AwsStorageForm | null>(null)
const wasabiStorageForm = ref<typeof WasabiStorageForm | null>(null)
const gcpStorageForm = ref<typeof GcpStorageForm | null>(null)
const azureStorageForm = ref<typeof AzureStorageForm | null>(null)
const minioStorageForm = ref<typeof MinioStorageForm | null>(null)

const currentForm = computed(() => {
  switch (provider.value) {
    case StorageProvider.AWS:
      return awsStorageForm.value
    case StorageProvider.Wasabi:
      return wasabiStorageForm.value
    case StorageProvider.GCP:
      return gcpStorageForm.value
    case StorageProvider.Azure:
      return azureStorageForm.value
    case StorageProvider.Minio:
      return minioStorageForm.value
    default:
      return null
  }
})

const isValidatingStorage = ref(false)
const isCreatingStorage = ref(false)

const confirmText = computed(() => {
  if (isValidatingStorage.value) return 'Validating storage...'
  if (isCreatingStorage.value) return 'Creating storage...'
  return 'Confirm'
})

const close = (): void => {
  updateOpen(false)
  emit('closed')
}

const onSubmitGlobal = (): void => {
  currentForm.value?.onSubmit()
}

const teamStore = useTeamStore()
const storageStore = useExternalStorageStore()
const billingInfoLoader = useBillingInfoLoader()

const onSubmit = async (storage: StoragePayload): Promise<void> => {
  if (!teamStore.currentTeam) return

  isValidatingStorage.value = true

  const teamId = teamStore.currentTeam.id
  const teamSlug = teamStore.currentTeam.slug

  try {
    const payload = {
      storage: { ...storage, provider: provider.value },
      teamSlug,
    }

    // Validate storage configuration before creating or updating
    const validationResult = await validateStorage(payload)

    isValidatingStorage.value = false
    isCreatingStorage.value = true

    if (!validationResult.ok) {
      const message =
        validationResult.error.message && typeof validationResult.error.message === 'string'
          ? validationResult.error.message
          : 'Storage validation failed'

      toast.warning({ meta: { title: message } })
      return
    }

    const result = await (props.storage ? updateStorage(payload) : createStorage(payload))

    if (!result.ok) {
      const message =
        // showing "ValidationError" toast is not great, but there is currently no support for
        // error fields in form
        result.error && !result.error.message
          ? 'Validation error'
          : (result.error.message as string)

      toast.warning({ meta: { title: message } })
      return
    }

    // push or replace in store, based on whether it was an update or create
    if (props.storage) {
      const index = storageStore.storages.findIndex((s) => s.slug === result.data.slug)
      storageStore.storages.splice(index, 1, result.data)
    } else {
      storageStore.storages.push(result.data)
    }

    // potentially make additional request to set default
    // note that unsetting default currently does nothing
    if (storage.default) {
      const setDefaultResult = await setStorageAsDefault({
        teamSlug,
        storageSlug: result.data.slug,
      })

      if (setDefaultResult.ok) {
        storageStore.storages = storageStore.storages.map((s) => ({
          ...s,
          // backend generates the slug,
          // so we take it from `result.data.slug`, not `storage.slug`
          default: s.slug === result.data.slug,
        }))
      }
    }

    await billingInfoLoader.load(teamId)
    close()
  } finally {
    isValidatingStorage.value = false
    isCreatingStorage.value = false
  }
}
</script>

<style lang="scss" scoped>
.form__content {
  position: relative;
  padding: 15px;
}

.storage__field {
  margin-bottom: 16px;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.storage-creation-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.storage-creation-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
